import {
	Ban,
	Bot,
	CheckSquare,
	Eraser,
	GitPullRequestDraft,
	Info,
	Moon,
	Scale,
	TriangleAlert,
} from "lucide-react";
import React from "react";
import { isStale } from "../utils";
import { Tooltip, TooltipBadgeContent } from "./Tooltip";

const statusTooltip = (message, badge) => (
	<TooltipBadgeContent badge={badge}>{message}</TooltipBadgeContent>
);

export const MRStatusIcons = ({ mr, iconClass = "w-4 h-4" }) => {
	if (!mr) return null;

	let mrSizeIcon = <></>;
	if (mr.diffStatsSummary) {
		const changedLines =
			mr.diffStatsSummary.additions + mr.diffStatsSummary.changes;
		if (changedLines > 5000) {
			mrSizeIcon = (
				<Tooltip
					content={statusTooltip("Large MR: over 5000 lines changed.", "Size")}
				>
					<Scale className={`${iconClass} text-amber-500`} />
				</Tooltip>
			);
		} else if (mr.diffStatsSummary.deletions > 2 * changedLines) {
			mrSizeIcon = (
				<Tooltip
					content={statusTooltip(
						"Cleanup MR: primarily removing or replacing code.",
						"Size",
					)}
				>
					<Eraser className={`${iconClass} text-[var(--color-accent)]`} />
				</Tooltip>
			);
		}
	} else {
		mrSizeIcon = (
			<Tooltip content={statusTooltip("Could not retrieve MR size.", "Size")}>
				<Info className={`${iconClass} text-amber-500`} />
			</Tooltip>
		);
	}

	return (
		<>
			{mrSizeIcon}

			{isStale(mr) && (
				<Tooltip
					content={statusTooltip(
						"Stale MR: no activity for over 14 days.",
						"Age",
					)}
				>
					<Moon className={`${iconClass} text-[var(--color-accent)]`} />
				</Tooltip>
			)}

			{mr.conflicts && (
				<Tooltip
					content={statusTooltip(
						"Source and target branches conflict.",
						"Merge",
					)}
				>
					<Ban className={`${iconClass} text-red-500`} />
				</Tooltip>
			)}

			{mr.additionalRules?.blocking?.length > 0 && (
				<Tooltip
					content={statusTooltip(
						"Project rules may block review or merge.",
						"Rules",
					)}
					listItems={mr.additionalRules.blocking}
				>
					<span className="inline-flex items-center text-red-500">
						<TriangleAlert className={iconClass} />
						<span className="ml-1 text-xs font-medium">
							{mr.additionalRules.blocking.length}
						</span>
					</span>
				</Tooltip>
			)}

			{mr.additionalRules?.info?.length > 0 && (
				<Tooltip
					content={statusTooltip(
						"Project rules may affect review or merge.",
						"Rules",
					)}
					listItems={mr.additionalRules.info}
				>
					<span className="inline-flex items-center text-[var(--color-accent)]">
						<Info className={iconClass} />
						<span className="ml-1 text-xs font-medium">
							{mr.additionalRules.info.length}
						</span>
					</span>
				</Tooltip>
			)}

			{mr.discussions > 0 && (
				<Tooltip content={statusTooltip("Blocking discussions.", "Thread")}>
					<span className="inline-flex items-center text-yellow-500">
						<CheckSquare className={iconClass} />
						<span className="ml-1 text-xs font-medium">{mr.discussions}</span>
					</span>
				</Tooltip>
			)}
		</>
	);
};

export const BotIcon = ({ author, iconClass = "w-4 h-4" }) => {
	if (!author?.bot) return null;

	return (
		<Tooltip content={statusTooltip("Bot author.", "Author")}>
			<Bot className={`${iconClass} ml-1 text-[var(--color-text-tertiary)]`} />
		</Tooltip>
	);
};

export const DraftIcon = ({ draft, iconClass = "w-4 h-4" }) => {
	if (!draft) return null;

	return (
		<Tooltip content={statusTooltip("Draft merge request.", "State")}>
			<GitPullRequestDraft
				className={`${iconClass} text-[var(--color-text-tertiary)]`}
			/>
		</Tooltip>
	);
};
